import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import useAppContext from '../../../context/useAppContext';
import { ToastContext, Button, Tooltip, ToggleContainer } from '@weezevent/nacre';

import { MissingEventMessage } from './missingEventMessage';

export function QueueCTATooltip({ disabled, children }) {
    const { t } = useTranslation();
    const help = disabled ? t('ticket.widget.queue_step.redirect_to_queue_help') : t('ticket.widget.queue_step.redirect_to_queue_tip');

    return (
        <Tooltip margin={false} triggerOnHover title={help}>
            {children}
        </Tooltip>
    );
}

QueueCTATooltip.propTypes = {
    disabled: PropTypes.bool,
    children: PropTypes.node
};

export default function QueueStepConfig({ steps, step, widget, config = {}, onUpdate }) {
    const { t } = useTranslation();

    const [{ organization }] = useAppContext();
    const toast = React.useContext(ToastContext);

    const disabled = !widget.events.length;

    const countdown_step = steps.find(step => /countdown/gi.test(step.slug));

    const soldout = steps.find(step => /soldout/gi.test(step.slug));

    const is_real_soldout_enabled = React.useMemo(() => {
        if (!countdown_step || !step || !soldout) {
            return false;
        }

        return countdown_step.success_step === soldout.id && step.success_step === soldout.id;
    }, [countdown_step, step, soldout]);

    return (
        <div>
            <br />
            <MissingEventMessage config={config} />
            <QueueCTATooltip disabled={disabled}>
                <Button
                    primary
                    disabled={!config.event_id}
                    label={t('ticket.widget.queue_step.redirect_to_queue_settings')}
                    onClick={() => {
                        const { event_id } = config;
                        window.open(`${window.location.origin}/ticket/O${organization.id}/E${event_id}/settings/queue/${widget.widget_key}`);
                    }}
                />
            </QueueCTATooltip>
            <div style={{ marginTop: '15px' }}>
                <ToggleContainer
                    disabled={!config.event_id}
                    checked={config['virtual_soldout']}
                    title={t('ticket.widget.queue_step.virtual_soldout_button')}
                    subtitle={t('ticket.widget.queue_step.virtual_soldout_tooltip')}
                    onChange={(synth, { checked }) => {
                        const newConfig = {
                            ...config,
                            virtual_soldout: checked
                        };
                        step.config = newConfig;
                        step.save({
                            widgetId: widget.id,
                            organizationId: organization.id
                        })
                            .then(() => {
                                onUpdate();
                                toast.success(t('ticket.widget.queue_step.virtual_soldout_applied_success'));
                            })
                            .catch(e => {
                                toast.error(t('ticket.widget.queue_step.virtual_soldout_applied_error'));
                            });
                    }}
                />

                <ToggleContainer
                    disabled={!countdown_step || !step || !soldout}
                    checked={is_real_soldout_enabled}
                    title={t('ticket.widget.queue_step.real_soldout_button')}
                    subtitle={t('ticket.widget.queue_step.real_soldout_tooltip')}
                    onChange={(synth, { checked }) => {
                        const params = {
                            widgetId: widget.id,
                            organizationId: organization.id
                        };

                        // Set config for real_soldout
                        // Allows getting out of queue
                        const newConfig = {
                            ...config,
                            real_soldout: checked
                        };

                        step.config = newConfig;

                        if (!checked) {
                            const limitator_step = steps.find(step => /limitator/gi.test(step.slug));
                            const redirect_step = steps.find(step => /redirect/gi.test(step.slug));
                            countdown_step.success_step = limitator_step.id;
                            step.success_step = redirect_step.id;
                        } else {
                            countdown_step.success_step = soldout.id;
                            step.success_step = soldout.id;
                        }

                        const p_countdown = countdown_step.save(params);
                        const p_queue = step.save(params);

                        return Promise.all([p_countdown, p_queue])
                            .then(() => {
                                // toast and sync
                                onUpdate();
                                toast.success(t('ticket.widget.queue_step.real_soldout_applied_success'));
                            })
                            .catch(e => {
                                toast.success(t('ticket.widget.queue_step.real_soldout_applied_error'));
                            });
                    }}
                />
            </div>
        </div>
    );
}

QueueStepConfig.propTypes = {
    step: PropTypes.object.isRequired,
    widget: PropTypes.object.isRequired,
    config: PropTypes.object
};
